import { Suspense, lazy, useEffect, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { ThemeProvider } from '@/components/theme-provider'
import { BRAND } from '@/config/brand'
const Navbar = lazy(() => import('@/components/layout/navbar').then((m) => ({ default: m.Navbar })))
const Footer = lazy(() => import('@/components/layout/footer').then((m) => ({ default: m.Footer })))
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { AuthProvider, useAuth } from '@/context/AuthContext'
import { I18nProvider } from '@/context/LanguageContext'
import { isFeatureEnabled } from '@/lib/featureFlags'
import { useIdleTimer } from '@/hooks/useIdleTimer'
import { HOME_PATH, resolveRootDestination } from '@/lib/routing'
import { RETURN_CONTEXT_KEY } from '@/types/navigation'
import { NavigationErrorBoundary } from '@/features/navigation/components/navigation-error-boundary'

const HomePage = lazy(() => import('@/pages/Home'))
const MapPage = lazy(() => import('@/pages/MapPage'))
const AuditPage = lazy(() => import('@/pages/Audit'))
const CreditsPage = lazy(() => import('@/pages/Credits'))
const ExplorationPage = lazy(() => import('@/pages/Exploration'))
const ProfilePage = lazy(() => import('@/pages/Profile'))
const RegisterPage = lazy(() => import('@/pages/Register'))
const AuthCallbackPage = lazy(() => import('@/pages/AuthCallback'))
const PaymentReturnPage = lazy(() => import('@/pages/PaymentReturnPage'))
const CertificatesPage = lazy(() => import('@/pages/Certificates'))
const CitizenReportPage = lazy(() => import('@/pages/CitizenReport'))
const FireHistoryPage = lazy(() => import('@/pages/FireHistory'))
const FireDetailPage = lazy(() => import('@/pages/FireDetail'))
const LoginPage = lazy(() => import('@/pages/Login'))
const SheltersPage = lazy(() => import('@/pages/Shelters'))
const FaqPage = lazy(() => import('@/pages/faq'))
const ManualPage = lazy(() => import('@/pages/manual'))
const GlossaryPage = lazy(() => import('@/pages/glossary'))
const ContactPage = lazy(() => import('@/pages/contact'))
const NotFoundPage = lazy(() => import('@/pages/NotFound'))
const MonitoringDashboardPage = lazy(() =>
  import('@/pages/MonitoringDashboard').then((m) => ({ default: m.default })),
)

function AppLoading() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center gap-2 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
      <span className="text-sm">Loading...</span>
    </div>
  )
}

function RootRouteGate() {
  const { status } = useAuth()
  const destination = resolveRootDestination(status)

  if (destination === null) {
    return <AppLoading />
  }

  return <Navigate to={destination} replace />
}

function AppShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { isAuthenticated, signOut } = useAuth()
  const isMapPage = pathname === '/map'
  const hideChrome = pathname === '/login' || pathname === '/register' || pathname === '/'
  const shellClass = isMapPage ? 'flex h-screen md:min-h-screen flex-col' : 'flex min-h-screen flex-col'
  const mainClass = hideChrome
    ? 'flex-1'
    : isMapPage
      ? 'flex-1 overflow-hidden md:overflow-visible pt-0 pb-11 md:pt-24 md:pb-0'
      : 'flex-1 pt-0 pb-24 md:pt-24 md:pb-0'

  useIdleTimer({
    enabled: isAuthenticated,
    onIdle: async () => {
      await signOut()
      try { sessionStorage.removeItem(RETURN_CONTEXT_KEY) } catch { /* ignore storage errors */ }
      navigate('/login', { state: { reason: 'idle' } })
    },
  })

  return (
    <div className={shellClass}>
      {!hideChrome && (
        <NavigationErrorBoundary>
          <Suspense fallback={null}>
            <Navbar />
          </Suspense>
        </NavigationErrorBoundary>
      )}
      <main className={mainClass}>{children}</main>
      {!hideChrome && (
        <Suspense fallback={null}>
          <Footer />
        </Suspense>
      )}
    </div>
  )
}

export default function App() {
  useEffect(() => {
    document.title = `${BRAND.name} | Monitoreo de incendios forestales`
  }, [])

  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppShell>
              <Suspense fallback={<AppLoading />}>
                <Routes>
                  <Route path="/" element={<RootRouteGate />} />
                  <Route path={HOME_PATH} element={<HomePage />} />
                  <Route path="/map" element={<MapPage />} />
                  <Route
                    path="/audit"
                    element={
                      <ProtectedRoute>
                        <AuditPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/credits"
                    element={
                      <ProtectedRoute>
                        <CreditsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/exploracion"
                    element={<ExplorationPage />}
                  />
                  <Route path="/reports" element={<Navigate to="/exploracion" replace />} />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <ProfilePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/payments/return" element={<PaymentReturnPage />} />
                  <Route
                    path="/certificates"
                    element={isFeatureEnabled('certificates') ? <CertificatesPage /> : <NotFoundPage />}
                  />
                  <Route path="/citizen-report" element={<CitizenReportPage />} />
                  <Route path="/fires" element={<Navigate to="/fires/history" replace />} />
                  <Route
                    path="/fires/history"
                    element={
                      <ProtectedRoute>
                        <FireHistoryPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/fires/:id" element={<FireDetailPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/auth/callback" element={<AuthCallbackPage />} />
                  <Route
                    path="/shelters"
                    element={isFeatureEnabled('refuges') ? <SheltersPage /> : <NotFoundPage />}
                  />
                  <Route path="/faq" element={<FaqPage />} />
                  <Route path="/manual" element={<ManualPage />} />
                  <Route path="/glossary" element={<GlossaryPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/monitoring" element={<MonitoringDashboardPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </AppShell>
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  )
}
